import * as helper from './helper/index.js';
import * as date from './date.js';

import * as rpc from './rpc.js';
import * as fp from './fp/index.js';
import * as algo from './algo/index.js';
import * as ds from './ds/index.js';
import * as log from './log.js';
import * as jsonPath from './json_path.js';
import * as time from './time.js';

const sleep = (t) => {
  return new Promise((resolve) => {
    setTimeout(resolve, t);
  });
};

const once = (fn) => {
  let p = null;
  return async (...args) => {
    if (p !== null) return p;
    p = Promise.resolve(fn(...args));
    const ans = await p;
    p = null; // eslint-disable-line
    return ans;
  };
};

const retry = (fn, max = 0, duration = 100) => {
  return async (...args) => {
    for (let i = 0; i < max + 1; i++) {
      try {
        return await fn(...args);
      } catch (err) {
        await sleep(duration);
        if (i === max + 1) throw err;
      }
    }
  };
};

/**
 * continuous running task,
 * 1. will only continue after finishing current task
 * 2. only run once even if get many requests during current task execution period
 */
const continuous = (fn) => {
  let nextFlag = false,
    p = null;

  let run = async (...args) => {
    if (nextFlag && p === null) {
      nextFlag = false;

      try {
        p = Promise.resolve(fn(...args));
        await p;
      } catch (err) {
        //
      }

      p = null;
      return run();
    }
  };

  return (...args) => {
    nextFlag = true;
    run(...args);
  };
};

/**
 * control the concurrency
 */
const concurrent = ({
  max = 1024
} = {}) => {
  const taskQueue = [];
  let con = 0;

  const runTask = async () => {
    if (con >= max) return;

    if (!taskQueue.length) return;

    const {
      resolve,
      reject,
      fn
    } = taskQueue.shift();

    con++;
    try {
      resolve(await fn());
    } catch (err) {
      reject(err);
    }
    con--;
    runTask();
  };

  return (fn) => {
    return new Promise((resolve, reject) => {
      const task = {
        resolve,
        reject,
        fn
      };

      taskQueue.push(task);

      runTask();
    });
  };
};

const bindOnConcurrent = (concur, fn) => {
  return (...args) => {
    return concur(() => {
      return fn(...args);
    });
  };
};

const delayQueue = (fn, delayTime) => {
  const queue = [];

  let flag = false;

  const handleQueue = async () => {
    if (queue.length === 0 || flag) {
      return;
    }
    flag = true;

    const first = queue.shift();
    try {
      const ans = await first.fn(...first.args);
      first.resolve(ans);
    } catch (err) {
      first.reject(err);
    }

    // sleep
    await sleep(delayTime);

    flag = false;
    handleQueue();
  };

  return (...args) => {
    return new Promise((resolve, reject) => {
      queue.push({
        resolve,
        reject,
        args,
        fn
      });

      handleQueue();
    });
  };
};

const toPrecision = (v, precision) => {
  if (typeof precision !== 'number') {
    throw new Error(`precision needs to be a number, but got ${precision}`);
  }
  const base = Math.pow(10, precision);
  return Math.floor(v * base) / base;
};

// Joins path segments.  Preserves initial '/' and resolves '..' and '.'
// Does not support using '..' to go above/outside the root.
// This means that join('foo', '../../bar') will not resolve to '../bar'
function join( /* path segments */ ) {
  // Split the inputs into a list of path commands.
  var parts = [];
  for (var i = 0, l = arguments.length; i < l; i++) {
    parts = parts.concat(arguments[i].split('/'));
  }
  // Interpret the path commands to get the new resolved path.
  var newParts = [];
  for (i = 0, l = parts.length; i < l; i++) {
    var part = parts[i];
    // Remove leading and trailing slashes
    // Also remove '.' segments
    if (!part || part === '.') continue;
    // Interpret '..' to pop the last segment
    if (part === '..') newParts.pop();
    // Push new path segments.
    else newParts.push(part);
  }
  // Preserve the initial slash if there was one.
  if (parts[0] === '') newParts.unshift('');
  // Turn back into a single string path.
  return newParts.join('/') || (newParts.length ? '/' : '.');
}

import * as id from './id.js';

// Default export for backward compatibility
const spherelibUtils = {
  sleep,
  once,
  retry,
  continuous,
  concurrent,
  bindOnConcurrent,
  delayQueue,
  toPrecision,
  helper,
  date,
  join,

  fp,
  rpc,
  algo,
  log,
  ds,
  jsonPath,
  time,

  id,
};

export default spherelibUtils;

export {
  sleep,
  once,
  retry,
  continuous,
  concurrent,
  bindOnConcurrent,
  delayQueue,
  toPrecision,
  helper,
  date,
  join,

  fp,
  rpc,
  algo,
  log,
  ds,
  jsonPath,
  time,

  id,
};
